import { defineStore } from 'pinia';
import { styleZoom, valueToPx } from './builder-utils';
// builder.store.js imports useBuilderStyles back, but both are Pinia composables only invoked inside
// function bodies (never at module-eval time), so the cycle is inert at runtime.
// eslint-disable-next-line import/no-cycle
import { useBuilder } from './builder.store';

const zoomStep = 10;

export const DOCUMENT_PDF = 'pdf';

export const fontFamilies = [
	'"Times New Roman", Times, serif',
	'"Nunito", sans-serif',
	'"Noto Sans", sans-serif',
	'"Niramit", sans-serif',
	'"Merriweather", serif',
	'"EB Garamond", serif',
	'Arial, "Times New Roman", Times, serif',
];

export const useBuilderStyles = defineStore('pdf-builder-styles', {
	state: () => ({
		// used for resume margins
		defaultWidth: 800,
		leftMargin: 60,
		rightMargin: 60,
		topMargin: 36,
		bottomMargin: 36,

		isPreview: false, 

		zoom: 100,
		fontFamily: fontFamilies[0],
		textSize: 0,
		lineHeight: 3.5,
	}),
	actions: {
		changeMargin(newMargin, side) {
			if (side === 'left') {
				this.leftMargin = (newMargin * 100) / this.zoom;
			} else {
				this.rightMargin = this.defaultWidth - ((newMargin * 100) / this.zoom);
			}

			this.save();
		},
		updateMargins(margins) {
			this.leftMargin = margins.left;
			this.rightMargin = margins.right;
			this.topMargin = margins.top;
			this.bottomMargin = margins.bottom;
        
			this.save();
		},
		applyFont(fontFamily) {
			this.fontFamily = fontFamily;
			this.save();
		},
		zoomOut() {
			const value = this.zoom - zoomStep;
			this.zoom = Math.max(value, 50);
		},
		zoomIn() {
			const value = this.zoom + zoomStep;
			this.zoom = Math.min(value, 170);
		},
		resetZoom() {
			this.zoom = 100;
		},
		applyFontSize(fontSize) {
			this.textSize = Math.max(fontSize - 14, -5);
			this.save();
		},
		textMinus() {
			const value = this.textSize - 1;
			this.textSize = Math.max(value, -5);

			this.save();
		},
		textPlus() {
			const value = this.textSize + 1;
			this.textSize = Math.min(value, 5);

			this.save();
		},
		resetText() {
			if (this.textSize === 0) {return;}

			this.textSize = 0;
			this.save();
		},
		resetMargins() {
			this.leftMargin = 60;
			this.rightMargin = 60;
			this.topMargin = 36;
			this.bottomMargin = 36;
			this.save();
		},
		setLineHeight(lineHeight) {
			this.lineHeight = parseFloat(lineHeight);

			this.save();
		},
		lineHeightPlus() {
			const value = this.lineHeight + 0.5;
			this.lineHeight = Math.min(value, 10);

			this.save();
		},
		resetLineHeight() {
			if (this.lineHeight === 3.5) {return;}

			this.lineHeight = 3.5;
			this.save();
		},
		save() {
			useBuilder().save();
		},
		applyStyles(styles) {
			const {
				font_family,
				text_size,
				line_height,
				left_margin,
				right_margin,
				top_margin,
				bottom_margin
			} = styles;

			this.fontFamily = font_family || fontFamilies[0];
			this.textSize = text_size || 0;
			this.lineHeight = line_height || 3.5;
			this.leftMargin = left_margin || 60;
			this.rightMargin = right_margin || 60;
			this.topMargin = top_margin || 36;
			this.bottomMargin = bottom_margin || 36;
		}
	},
	getters: {
		getBuilderWidth({ zoom, defaultWidth }) {
			return styleZoom(defaultWidth, zoom);
		},
		getRullerPaddings({ zoom, leftMargin, rightMargin }) {
			const leftPadding = styleZoom(leftMargin, zoom);
			const rightPadding = styleZoom(rightMargin, zoom);

			return { leftPadding, rightPadding };
		},
		ruler({ zoom, defaultWidth }) {
			const value = valueToPx(styleZoom(defaultWidth, zoom));
      
			return {
				minWidth: value,
				maxWidth: value,
				width: value,
			};
		},
		page({ zoom, defaultWidth, leftMargin, rightMargin, topMargin, bottomMargin }) {
			const value = valueToPx(styleZoom(defaultWidth, zoom));

			const [topPadding, bottomPadding, leftPadding, rightPadding] = [
				valueToPx(styleZoom(topMargin, zoom)),
				valueToPx(styleZoom(bottomMargin, zoom)),
				valueToPx(styleZoom(leftMargin, zoom)),
				valueToPx(styleZoom(rightMargin, zoom)),
			];

			const radius = valueToPx(Math.max(styleZoom(14, zoom), 2));

			return {
				minWidth: value,
				maxWidth: value,
				width: value,
				padding: `${topPadding} ${rightPadding} ${bottomPadding} ${leftPadding}`,
				borderRadius: radius,
			};
		},
		actionIcon({ zoom }) {
			const value = valueToPx(styleZoom(16, zoom));

			return {
				width: value,
				height: value,
				left: '100%'
			};
		},
		nameStyles({ zoom, fontFamily }) {
			const fontSize = valueToPx(styleZoom(24 + this.textSize, zoom));

			return {
				fontSize,
				fontFamily,
			};
		},
		currentFontFamily({ fontFamily }) {
			return fontFamily.split(',')[0].replaceAll('"', '');
		},
		contactStyles({ zoom, fontFamily }) {
			const fontSize = valueToPx(styleZoom(14 + this.textSize, zoom));
			const lineHeight = valueToPx(styleZoom(17 + this.lineHeight, zoom));

			return { fontSize, fontFamily, lineHeight };
		},
		sectionHeading({ zoom }) {
			const width = valueToPx(Math.max(styleZoom(1.7094, zoom), 1));

			return {
				paddingBottom: width,
				borderBottom: `${width} solid #000000`,
			};
		},
		sectionStyle({ zoom }) {
			const margin = valueToPx(Math.max(styleZoom(16, zoom), 4));

			return {
				marginBottom: margin,
			};
		},
		sectionPart({ zoom }) {
			const margin = valueToPx(Math.max(styleZoom(16, zoom), 2));

			return {
				marginTop: margin,
			};
		},
		bulletText({ zoom, fontFamily }) {
			const fontSize = valueToPx(styleZoom(14 + this.textSize, zoom));
			const lineHeight = valueToPx(styleZoom(17 + this.lineHeight, zoom));
			const padding = '';

			return { fontSize, paddingLeft: padding, fontFamily, lineHeight };
		},
		sectionTitle({ zoom, fontFamily }) {
			const size = valueToPx(styleZoom(14 + this.textSize, zoom));
			const lineHeight = valueToPx(styleZoom(17 + this.lineHeight, zoom));

			return {
				fontSize: size,
				textTransform: 'uppercase',
				fontFamily,
				lineHeight,
			};
		},
		button({ zoom, fontFamily }) {
			const fontSize = valueToPx(styleZoom(14 + this.textSize, zoom));
			const horizontal = valueToPx(styleZoom(20, zoom));
			const height = valueToPx(styleZoom(24, zoom));
			const radius = valueToPx(styleZoom(4, zoom));

			return {
				fontSize,
				height,
				paddingLeft: horizontal,
				paddingRight: horizontal,
				paddingTop: 0,
				paddingBottom: 0,
				borderRadius: radius,
				fontFamily,
			};
		},
		buttonIcon({ zoom }) {
			const size = valueToPx(styleZoom(16, zoom));

			return {
				width: size,
				height: size,
			};
		},
		inputValueCoverLetter({ zoom, fontFamily }) {
			const size = valueToPx(styleZoom(16 + this.textSize, zoom));
			const lineHeight = valueToPx(styleZoom(22 + this.lineHeight, zoom));

			return {
				lineHeight: lineHeight,
				fontSize: size,
				fontFamily,
			};
		},
		inputValue({ zoom, fontFamily }) {
			const size = valueToPx(styleZoom(14 + this.textSize, zoom));

			return {
				fontSize: size,
				fontFamily,
				lineHeight: valueToPx(styleZoom(17 + this.lineHeight, zoom))
			};
		},
		inputLabel() {
			return {
				...this.inputValue,
				fontWeight: '700',
			};
		},
		addSection({ zoom }) {
			return {
				fontSize: valueToPx(styleZoom(14 + this.textSize, zoom)),
				height: valueToPx(styleZoom(32, zoom)),
				borderRadius: valueToPx(styleZoom(4, zoom)),
				marginTop: valueToPx(styleZoom(14, zoom)),
			};
		},
		bulletTrigger({ zoom }) {
			const size = valueToPx(styleZoom(23.92, zoom));

			return {
				width: size,
				height: size,
			};
		}
	}
});